<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="individualCustomerSuccessCount.length || individualCustomerFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="individualCustomerSuccessCount.length"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ individualCustomerSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="individualCustomerFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="individualCustomerFailedReport"
            :items="individualCustomerFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="batchIndividualCustomerRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col
              md="3"
              class="mt-2"
            >
              <SwitchButton
                v-model="enablePreview"
                :label="$t('Enable Preview?')"
                name="enable-preview"
              />
            </b-col>
            <b-col md="3">
              <Button
                v-if="getUserPermission('BATCH_ACCOUNT_UPLOAD')"
                :label="$t('Create Customers')"
                size="md"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleSubmit"
              />
            </b-col>
            <b-col md="6">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Batch Individual Customer Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="individualCustomerSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows && !enablePreview"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
    <b-table
      v-if="enablePreview"
      ref="glCustomerPostingRef"
      :items="parseExcelFile"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      name="table-pagination"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import XLSX from 'xlsx'
import TablePagination from '@/views/components/table/TablePagination.vue'
import Button from '../../components/button/Button.vue'
import SwitchButton from '../../components/switch/Switch.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../helpers/data'
import { toYearWithDate } from '../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BAlert,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    SwitchButton,
    TablePagination,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      enablePreview: false,
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      // tableHeaders: [
      // { key: 'SN', label: 'S/N' },
      //   { key: 'oldCustomerID', sortable: true, label: 'Old Customer ID' },
      // ],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'oldCustomerID', sortable: true, label: 'Old Customer ID' },
        { key: 'customerType', sortable: true, label: 'Customer Type' },
        { key: 'title', sortable: true, label: 'Title' },
        { key: 'lastName', sortable: true, label: 'Last Name' },
        { key: 'firstName', sortable: true, label: 'First Name' },
        { key: 'otherNames', sortable: true, label: 'Other Names' },
        { key: 'gender', sortable: true, label: 'Gender' },
        { key: 'address', sortable: true, label: 'Address' },
        { key: 'maritalStatus', sortable: true, label: 'Marital Status' },
        { key: 'maidenName', sortable: true, label: 'Maiden Name' },
        { key: 'religion', sortable: true, label: 'Religion' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email' },
        { key: 'hometown', sortable: true, label: 'Home Town' },
        { key: 'dateOfBirth', sortable: true, label: 'Date Of Birth' },
        { key: 'nationality', sortable: true, label: 'Nationality' },
        { key: 'state', sortable: true, label: 'State' },
        { key: 'lga', sortable: true, label: 'LGA' },
        { key: 'meansOfIdentification', sortable: true, label: 'Means Of Identification' },
        { key: 'meansOfIdentificationNumber', sortable: true, label: 'Means Of Identification Number' },
        { key: 'nokName', sortable: true, label: 'Next Of Kin Name' },
        { key: 'nokAddress', sortable: true, label: 'Next Of Kin Address' },
        { key: 'nokGender', sortable: true, label: 'Next Of Kin Gender' },
        { key: 'nokRelationship', sortable: true, label: 'Next Of Kin Relationship' },
        { key: 'nokPhoneNumber', sortable: true, label: 'Next Of Kin Phone Number' },
        { key: 'nokEmail', sortable: true, label: 'Next Of Kin Email' },
        { key: 'occupation', sortable: true, label: 'Occupation' },
        { key: 'employerName', sortable: true, label: 'Employer Name' },
        { key: 'employerAddress', sortable: true, label: 'Employer Address' },
        { key: 'employerPhoneNumber', sortable: true, label: 'Employer Phone Number' },
        { key: 'bankVerificationNumber', sortable: true, label: 'Bank Verification Number' },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      individualCustomerSuccessCount: [],
      individualCustomerFailed: [],
      sortDesc: false,
      sortBy: 'lastName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      batchIndividualCustomer: 'batch/batchIndividualCustomer',
      downloadAccountFormat: 'batch/downloadAccountFormat',
    }),
    async handleSubmit() {
      this.$refs.batchIndividualCustomerRules.validate().then(success => {
        if (success) {
          this.confirm(this.batchIndividualCustomerAction, { text: this.$t('Are you sure you want to Create Batch Individual Customers?') })
        }
      })
    },
    async batchIndividualCustomerAction() {
      try {
        this.individualCustomerSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          gender: item.gender ? Number(item.gender) : null,
          email: item.email ? item.email : null,
          phoneNumber: item.phoneNumber ? item.phoneNumber : null,
          meansOfIdentificationNumber: item.meansOfIdentificationNumber ? item.meansOfIdentificationNumber : null,
          maritalStatus: item.maritalStatus ? Number(item.maritalStatus) : null,
          religion: item.religion ? Number(item.religion) : null,
          dateOfBirth: item.dateOfBirth ? toYearWithDate(item.dateOfBirth) : null,
          meansOfIdentification: item.meansOfIdentification ? Number(item.meansOfIdentification) : null,
          nokGender: item.nokGender ? Number(item.nokGender) : null,
        }))
        const response = await this.batchIndividualCustomer({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 1) {
            const plural = this.$t('Individual Customers was successfully created')
            this.individualCustomerSuccessCount = `(${data.successCount}) ${plural}`
          } else {
            const singular = this.$t('Individual Customer was successfully created')
            this.individualCustomerSuccessCount = `(${data.successCount}) ${singular}`
          }
          this.individualCustomerFailed = data.failed

          this.isDisabled = false
          this.isLoading = false
          this.totalSheetRows = ''
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const errorText = this.$t('Your request to create Batch Individual Customers failed. Reason')
          this.alertError({ text: `${errorText}: ${responseError.errorMessage}` })
        })
        this.isDisabled = false
        this.isLoading = false
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            oldCustomerID: items['Old Customer ID'],
            customerType: items['Customer Type'],
            title: items.Title,
            lastName: items['Last Name'],
            firstName: items['First Name'],
            otherNames: items['Other Names'],
            gender: items.Gender,
            address: items.Address,
            maritalStatus: items['Marital Status'],
            maidenName: items['Maiden Name'],
            religion: items.Religion,
            phoneNumber: items['Phone Number'],
            email: items.Email,
            homeTown: items['Home Town'],
            dateOfBirth: items['Date Of Birth'],
            nationality: items.Nationality,
            state: items.State,
            lga: items.LGA,
            meansOfIdentification: items['Means Of Identification'],
            meansOfIdentificationNumber: items['Means Of Identification Number'],
            nokName: items['Next Of Kin Name'],
            nokAddress: items['Next Of Kin Address'],
            nokGender: items['Next Of Kin Gender'],
            nokRelationship: items['Next Of Kin Relationship'],
            nokPhoneNumber: items['Next Of Kin Phone Number'],
            nokEmail: items['Next Of Kin Email'],
            occupation: items.Occupation,
            employerName: items['Employer Name'],
            employerAddress: items['Employer Address'],
            employerPhoneNumber: items['Employer Phone Number'],
            bankVerificationNumber: items['Bank Verification Number'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Batch Individual Customers Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const INDIVIDUAL_CUSTOMER_BIODATA_UPLOAD_TEMPLATE = 21
        const response = await this.downloadAccountFormat(INDIVIDUAL_CUSTOMER_BIODATA_UPLOAD_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Batch-Account-Creation.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$('Your request to download Batch Individual Customers Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const text = this.$t('Your request to download Batch Individual Customers Format failed. Reason')
          this.alertError({ text: `${text}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
